import { BusinessEntityDashboardDto } from '../types/businessEntities';
import { ActiveServiceRequestSummaryDto } from '../types/serviceRequests';

const LLC_APP_BASE_URL =
  process.env.REACT_APP_LLC_URL || 'http://localhost:3000/llc';

// Service-type names, copied verbatim from the LLC app's src/frontend/src/types/index.ts.
// Keep in sync with that file.
const LLC_CREATION_SERVICE_TYPE = 'LLC Creation';
const LLC_IMPORT_SERVICE_TYPE = 'LLC Import';
const DOMAIN_PURCHASE_SERVICE_TYPE = 'Domain Purchase and Business Email';
const DIGITAL_BANKING_SERVICE_TYPE = 'DAG Trading (By Uphold)';
const LLC_MAINTENANCE_SERVICE_TYPE = 'LLC Maintenance';
const LLC_COMPLIANCE_SERVICE_TYPE = 'DAG LLC Compliance';
const LLC_ANNUAL_REPORT_RENEWAL_SERVICE_TYPE = 'LLC Annual Report Renewal';

/**
 * A row that is only a paid intake purchase: the client paid but closed the form before
 * submitting, so no LLC or service request exists yet. Such a row has nothing to name or
 * link to except the purchase itself, so it reads and routes differently from every other
 * row on the dashboard.
 */
export function isUnfinishedIntakePurchase(
  entity: Pick<
    BusinessEntityDashboardDto,
    'intakePurchaseId' | 'businessEntityId' | 'serviceRequestId'
  >
): boolean {
  return (
    entity.intakePurchaseId != null &&
    entity.businessEntityId == null &&
    entity.serviceRequestId == null
  );
}

/**
 * Title for an unfinished-purchase row. An intake purchase is either an LLC Creation or an
 * LLC Maintenance / Wyoming-import bundle, and they resume into different forms — promising
 * "your LLC" to someone importing an existing business would misdescribe what Continue does.
 */
export function unfinishedIntakePurchaseTitle(
  serviceTypeName: string | null
): string {
  return serviceTypeName === LLC_CREATION_SERVICE_TYPE
    ? 'Finish creating your LLC'
    : 'Finish importing your business';
}

/**
 * Customer-facing type label for an unfinished-purchase row's subtitle. The import bundle is sold
 * as "LLC Maintenance" internally, but that's billing jargon to a client who is importing a
 * business — show "LLC Import" instead.
 */
export function unfinishedIntakePurchaseTypeLabel(
  serviceTypeName: string | null
): string {
  return serviceTypeName === LLC_CREATION_SERVICE_TYPE
    ? 'LLC Creation'
    : 'LLC Import';
}

/**
 * Resolves the import detail path from a business entity's import progress.
 * Shared by getLlcEntityUrl and getServiceRequestUrl. Returns null when there
 * is no businessEntityId to route to.
 */
function resolveImportPath(
  businessEntityId: number | null,
  isSelfServeImport: boolean,
  importDetailsSavedAt: string | null,
  importDocumentsSavedAt: string | null
): string | null {
  if (!businessEntityId) {
    return null;
  }
  if (isSelfServeImport && !importDocumentsSavedAt) {
    return importDetailsSavedAt
      ? `/finalize-import/${businessEntityId}`
      : `/self-serve-import/${businessEntityId}`;
  }
  return `/imported-business/${businessEntityId}`;
}

/**
 * Returns the full URL to the business entity's detail page in the LLC app,
 * or null if no navigable route exists for this entity's current state.
 * Mirrors the routing logic in src/frontend/src/constants/service-request-routing.ts.
 */
export function getLlcEntityUrl(
  entity: Pick<
    BusinessEntityDashboardDto,
    | 'hasServiceRequest'
    | 'serviceRequestId'
    | 'businessEntityId'
    | 'intakePurchaseId'
    | 'serviceTypeName'
    | 'status'
    | 'isSelfServeImport'
    | 'importDetailsSavedAt'
    | 'importDocumentsSavedAt'
  >
): string | null {
  let path: string | null = null;

  if (isUnfinishedIntakePurchase(entity)) {
    // The purchase is the only record so far — send them back to the form that
    // creates the LLC, naming the purchase so the right one gets finalized.
    path = `/intake/business-info?intakePurchaseId=${entity.intakePurchaseId}`;
  } else if (!entity.hasServiceRequest) {
    if (entity.businessEntityId) {
      path = `/imported-business/${entity.businessEntityId}`;
    }
  } else if (entity.serviceTypeName === LLC_IMPORT_SERVICE_TYPE) {
    path = resolveImportPath(
      entity.businessEntityId,
      entity.isSelfServeImport,
      entity.importDetailsSavedAt,
      entity.importDocumentsSavedAt
    );
  } else if (entity.serviceRequestId) {
    path =
      entity.status === 'Signature Required'
        ? `/llc-creation/${entity.serviceRequestId}/client-review`
        : `/llc-creation/${entity.serviceRequestId}`;
  }

  return path ? `${LLC_APP_BASE_URL}${path}` : null;
}

/**
 * Returns the full URL to a service request's detail page in the LLC app for the
 * dashboard's "Active Requests" section. Always returns a URL: a request with no
 * navigable detail page falls back to the LLC app root.
 *
 * Mirrors getServiceRequestDetailsUrl in
 * src/frontend/src/constants/service-request-routing.ts — keep the two in sync.
 * Two intentional divergences from that source:
 *   - LLC Maintenance appends `#billing` to scroll to the billing section.
 *   - A service type not enumerated here (or a state missing the required ids)
 *     falls back to the LLC app root rather than returning null.
 */
type ServiceRequestRoute = Pick<
  ActiveServiceRequestSummaryDto,
  | 'serviceRequestId'
  | 'businessEntityId'
  | 'serviceTypeName'
  | 'status'
  | 'needsPayment'
  | 'isSelfServeImport'
  | 'importDetailsSavedAt'
  | 'importDocumentsSavedAt'
>;

// Routing rules for getServiceRequestUrl, evaluated top to bottom: the first
// rule whose `match` returns true wins, and its `resolve` produces the path.
// Order is significant — LLC Import is checked before payment so import progress
// drives the destination.
const SERVICE_REQUEST_ROUTES: Array<{
  match: (req: ServiceRequestRoute) => boolean;
  resolve: (req: ServiceRequestRoute) => string | null;
}> = [
  {
    match: (req) => req.serviceTypeName === LLC_IMPORT_SERVICE_TYPE,
    resolve: (req) =>
      resolveImportPath(
        req.businessEntityId,
        req.isSelfServeImport,
        req.importDetailsSavedAt,
        req.importDocumentsSavedAt
      ),
  },
  {
    match: (req) => req.needsPayment,
    resolve: (req) => `/checkout?serviceRequestId=${req.serviceRequestId}`,
  },
  {
    match: (req) =>
      req.serviceTypeName === DOMAIN_PURCHASE_SERVICE_TYPE &&
      !!req.businessEntityId,
    resolve: (req) =>
      `/${req.businessEntityId}/purchase-domain?serviceRequestId=${req.serviceRequestId}`,
  },
  {
    match: (req) => req.serviceTypeName === LLC_CREATION_SERVICE_TYPE,
    resolve: (req) =>
      req.status === 'Signature Required'
        ? `/llc-creation/${req.serviceRequestId}/client-review`
        : `/llc-creation/${req.serviceRequestId}`,
  },
  {
    match: (req) => req.serviceTypeName === DIGITAL_BANKING_SERVICE_TYPE,
    resolve: (req) => `/dag-trading/${req.serviceRequestId}`,
  },
  {
    // The imported-business detail page renders for both formed and imported
    // businesses (it fetches purely by businessEntityId). #billing scrolls to
    // the RecurringBillingCard on that page.
    match: (req) =>
      req.serviceTypeName === LLC_MAINTENANCE_SERVICE_TYPE &&
      !!req.businessEntityId,
    resolve: (req) => `/imported-business/${req.businessEntityId}#billing`,
  },
  {
    match: (req) => req.serviceTypeName === LLC_COMPLIANCE_SERVICE_TYPE,
    resolve: (req) => `/llc-compliance/${req.serviceRequestId}`,
  },
  {
    match: (req) =>
      req.serviceTypeName === LLC_ANNUAL_REPORT_RENEWAL_SERVICE_TYPE,
    resolve: (req) => `/llc-annual-report/${req.serviceRequestId}`,
  },
];

export function getServiceRequestUrl(req: ServiceRequestRoute): string | null {
  const path =
    SERVICE_REQUEST_ROUTES.find((route) => route.match(req))?.resolve(req) ??
    null;

  // Anything that didn't match a route (an unenumerated service type, or a state
  // missing the required ids) falls back to the LLC app root so every Active
  // Requests row stays clickable. Registered Agent — the one service type with
  // no detail page — never reaches here: it's filtered out of the dashboard
  // list in useServiceRequests.
  return path === null ? `${LLC_APP_BASE_URL}` : `${LLC_APP_BASE_URL}${path}`;
}
